export const LINE_TERMS = [
    // ------------------------------------------------------------------ lines
    {
        id: 'cgl',
        term: 'Commercial general liability',
        short: 'CGL',
        aka: ['gl', 'general liability'],
        category: 'lines',
        essential: true,
        def: 'The core liability policy for a business. It pays when the business is legally liable for bodily injury or property damage to someone else, and it defends the business against those claims.',
        detail: 'A standard CGL has three insuring agreements: Coverage A (bodily injury and property damage), Coverage B (personal and advertising injury) and Coverage C (medical payments, paid without regard to fault). Defence is normally outside the limit, which makes a $1m CGL limit go much further than $1m on a professional policy.',
        confused: 'It does not cover the insured\'s own property, their employees\' injuries, their vehicles, or the quality of their own work. Those are property, workers\' comp, auto, and professional liability respectively.',
        see: ['coverage-b', 'products-completed-operations', 'occurrence', 'bop'],
    },
    {
        id: 'coverage-b',
        term: 'Personal and advertising injury',
        aka: ['coverage b'],
        category: 'liability',
        def: 'CGL Coverage B — liability for offences rather than accidents: libel, slander, false arrest, wrongful eviction, invasion of privacy, copyright infringement in an advertisement, and misappropriation of an advertising idea.',
        detail: 'It carries its own limit and its own set of exclusions, and it is where a surprising number of small-business claims land. Breach of contract, failure to conform to an advertised statement, and infringement of patent or trade secret are all excluded.',
        see: ['cgl', 'media-liability'],
    },
    {
        id: 'occurrence',
        term: 'Occurrence',
        category: 'liability',
        essential: true,
        def: 'In a liability policy, an accident — including continuous exposure to the same harmful conditions — that results in injury or damage the insured did not expect or intend.',
        detail: 'The "expected or intended" wording is why faulty workmanship fights are so common in construction: carriers argue defective work is a business risk rather than an accident, and courts across states disagree with each other about it.',
        see: ['occurrence-form', 'occurrence-limit', 'your-work'],
    },
    {
        id: 'bodily-injury',
        term: 'Bodily injury',
        short: 'BI',
        category: 'liability',
        essential: true,
        def: 'Physical injury, sickness or disease, including death. Most forms extend it to mental anguish arising from the physical injury.',
        detail: 'Purely emotional distress with no physical injury is usually not bodily injury under a standard CGL, which is one reason EPLI exists.',
        see: ['property-damage', 'cgl', 'epli'],
    },
    {
        id: 'property-damage',
        term: 'Property damage',
        short: 'PD',
        category: 'liability',
        essential: true,
        def: 'Physical injury to tangible property of someone else, including the loss of its use — plus loss of use of undamaged property.',
        detail: 'The word tangible does the work: electronic data is expressly not tangible property in current CGL forms, which is precisely the hole cyber policies were written to fill.',
        see: ['bodily-injury', 'cyber-liability', 'cgl'],
    },
    {
        id: 'products-completed-operations',
        term: 'Products-completed operations',
        aka: ['products liability', 'completed ops', 'pco'],
        category: 'liability',
        essential: true,
        def: 'Liability for injury or damage caused by a product after it has left the insured\'s hands, or by work after it has been finished.',
        detail: 'It is the coverage a contractor is judged on years later, and the reason completed-operations additional insured status and a long tail of policy years both matter on construction accounts.',
        see: ['products-completed-aggregate', 'your-work', 'additional-insured'],
    },
    {
        id: 'bop',
        term: 'Business owners policy',
        short: 'BOP',
        category: 'lines',
        essential: true,
        def: 'A packaged policy combining property and general liability for small and medium businesses, at a lower price than buying them separately.',
        detail: 'BOPs are eligibility-driven — square footage, receipts, class, number of stories — and the built-in extensions are often broader than a monoline package. Business income is usually included with no dollar limit but a twelve-month cap.',
        confused: 'A "package policy" is the general term. A BOP is a specific simplified, pre-packaged version of one.',
        see: ['package-policy', 'cgl', 'business-income'],
    },
    {
        id: 'package-policy',
        term: 'Package policy',
        aka: ['commercial package policy', 'cpp'],
        category: 'lines',
        def: 'Two or more coverage parts issued under one policy — typically property, liability, crime, inland marine and auto.',
        detail: 'Packaging normally earns a discount and gives one common expiration date, one dec page and one point of contact at the carrier.',
        see: ['bop', 'monoline', 'common-expiration-date'],
    },
    {
        id: 'monoline',
        term: 'Monoline',
        category: 'lines',
        def: 'A policy covering a single line of business on its own, rather than in a package.',
        detail: 'Carriers are often reluctant to write monoline liability or monoline auto, because the account is being shopped piece by piece and they get the pieces nobody else wanted.',
        see: ['package-policy', 'adverse-selection'],
    },
    {
        id: 'commercial-property',
        term: 'Commercial property',
        category: 'lines',
        essential: true,
        def: 'Covers the insured\'s own buildings and business personal property against damage — fire, wind, theft, water, and whatever else the chosen cause-of-loss form includes.',
        see: ['bpp', 'special-form', 'business-income', 'coinsurance'],
    },
    {
        id: 'bpp',
        term: 'Business personal property',
        short: 'BPP',
        aka: ['contents'],
        category: 'property',
        essential: true,
        def: 'Everything the business owns inside or near the building that is not the building itself — furniture, fixtures, machinery, stock, tools, and improvements the insured made to a space they rent.',
        see: ['commercial-property', 'tenants-improvements', 'personal-property-of-others'],
    },
    {
        id: 'tenants-improvements',
        term: 'Tenant improvements and betterments',
        aka: ['ti', 'improvements and betterments'],
        category: 'property',
        def: 'Alterations a tenant makes to a rented space that become part of the building — flooring, partitions, built-in fixtures. The tenant insures them under their own business personal property.',
        detail: 'Routinely uninsured, because the tenant assumes the landlord covers them and the landlord\'s policy covers only the original building. A build-out is the most commonly missed exposure on a new restaurant or retail account.',
        see: ['bpp', 'commercial-property'],
    },
    {
        id: 'personal-property-of-others',
        term: 'Personal property of others',
        aka: ['ppoo', 'bailee'],
        category: 'property',
        def: 'Property belonging to customers or other people that is in the insured\'s care, custody or control.',
        detail: 'A dry cleaner, a repair shop, a warehouse and a valet service all need this, and the CGL will not respond — care, custody and control is excluded there. Bailee coverage is the inland marine version.',
        see: ['care-custody-control', 'bailee-coverage', 'bpp'],
    },
    {
        id: 'business-income',
        term: 'Business income',
        aka: ['business interruption', 'bi coverage', 'time element'],
        category: 'property',
        essential: true,
        def: 'Replaces the profit a business would have earned, and pays its continuing expenses, while it is shut down by a covered property loss.',
        detail: 'Two things decide whether it works: the period of restoration (how long the carrier will pay, which ends when the property could reasonably be repaired, not when the customers come back) and the extended period of indemnity endorsement that covers the recovery afterwards.',
        confused: 'Business income replaces lost earnings. Extra expense pays the extra costs of staying open. Most accounts need both.',
        see: ['extra-expense', 'period-of-restoration', 'extended-period-of-indemnity', 'civil-authority'],
    },
    {
        id: 'extra-expense',
        term: 'Extra expense',
        category: 'property',
        def: 'Pays the additional costs of continuing to operate after a covered loss — renting temporary space, expediting repairs, leasing replacement equipment.',
        detail: 'For a business that cannot afford to close at all — a data centre, a medical practice — extra expense matters more than business income, and can be written alone.',
        see: ['business-income', 'period-of-restoration'],
    },
    {
        id: 'period-of-restoration',
        term: 'Period of restoration',
        category: 'property',
        def: 'The window business income and extra expense are paid for: from the loss until the property should reasonably be repaired or replaced, with due diligence.',
        detail: 'It is measured against a hypothetical reasonable repair, not the actual one. A slow contractor or a delayed insurance payment does not extend it.',
        see: ['business-income', 'extended-period-of-indemnity'],
    },
    {
        id: 'extended-period-of-indemnity',
        term: 'Extended period of indemnity',
        category: 'property',
        def: 'Continues business income coverage for a set number of days after the property is repaired, while the business earns its customers back.',
        detail: 'The base form gives 30 or 60 days. For a restaurant or a retailer that is nowhere near enough, and 180 or 365 days is cheap to add.',
        see: ['business-income', 'period-of-restoration'],
    },
    {
        id: 'civil-authority',
        term: 'Civil authority',
        category: 'property',
        def: 'Business income coverage for when a government order blocks access to the premises because of damage to *other* property nearby.',
        detail: 'It has three catches that decide most disputes: there must be physical damage somewhere, usually within a stated distance; there is often a waiting period; and it runs for a limited number of weeks.',
        see: ['business-income', 'ingress-egress'],
    },
    {
        id: 'ingress-egress',
        term: 'Ingress / egress',
        category: 'property',
        def: 'Coverage for lost income when physical damage prevents getting into or out of the premises, without a government order being involved.',
        see: ['civil-authority', 'business-income'],
    },
    {
        id: 'contingent-business-income',
        term: 'Contingent business income',
        aka: ['dependent properties', 'contingent bi'],
        category: 'property',
        def: 'Covers lost income when a supplier, a customer, or a business that draws traffic to yours suffers a covered loss.',
        detail: 'The four classic dependencies are contributing (a supplier), recipient (a customer), manufacturing, and leader (an anchor tenant). Usually the named business must be scheduled, so it is only as good as the schedule.',
        see: ['business-income', 'supply-chain'],
    },
    {
        id: 'inland-marine',
        term: 'Inland marine',
        category: 'lines',
        essential: true,
        def: 'Covers property that moves, property in transit, and property at locations the property policy does not reach — contractors equipment, tools, installation, fine art, computers, exhibitions.',
        detail: 'Inland marine is largely non-filed, meaning carriers write their own forms and rates. That makes it flexible and makes form comparison genuinely necessary rather than a formality.',
        confused: 'Nothing to do with boats — that is ocean marine. The name is historical: cargo that left the ship and continued inland.',
        see: ['ocean-marine', 'contractors-equipment', 'installation-floater', 'non-filed'],
    },
    {
        id: 'ocean-marine',
        term: 'Ocean marine',
        category: 'lines',
        def: 'Covers vessels, their cargo, and related liabilities on the water — hull, cargo, protection and indemnity, freight.',
        see: ['inland-marine', 'cargo', 'jones-act'],
    },
    {
        id: 'contractors-equipment',
        term: 'Contractors equipment',
        category: 'property',
        def: 'Inland marine coverage for mobile machinery, tools and equipment on job sites, in transit and in storage.',
        detail: 'Scheduled for larger items and blanketed for small tools, usually with a per-item limit. Rented and borrowed equipment is a separate extension and is what contract language usually requires.',
        see: ['inland-marine', 'installation-floater', 'scheduled-coverage'],
    },
    {
        id: 'installation-floater',
        term: 'Installation floater',
        category: 'property',
        def: 'Covers materials and equipment being installed at a job site — in transit, in storage and during installation — until the work is accepted.',
        confused: 'Builders risk covers the whole structure under construction. An installation floater covers one trade\'s materials within it.',
        see: ['builders-risk', 'inland-marine', 'contractors-equipment'],
    },
    {
        id: 'builders-risk',
        term: 'Builders risk',
        aka: ['course of construction', 'cocs'],
        category: 'lines',
        def: 'Property coverage for a structure while it is being built or renovated, including materials on site and often in transit.',
        detail: 'Written for the project term rather than twelve months, and it ends on a defined trigger — occupancy, acceptance, or a stated date — after which permanent property coverage must be in place. That handover is where gaps happen.',
        see: ['installation-floater', 'soft-costs', 'commercial-property'],
    },
    {
        id: 'soft-costs',
        term: 'Soft costs',
        category: 'property',
        def: 'On a builders risk policy, the non-construction costs of a delay caused by a covered loss — extra interest, additional architect and legal fees, extended permits, marketing.',
        see: ['builders-risk', 'business-income'],
    },
    {
        id: 'commercial-auto',
        term: 'Commercial auto',
        aka: ['business auto'],
        category: 'lines',
        essential: true,
        def: 'Covers vehicles used in the business — liability for accidents, and physical damage to the vehicles themselves.',
        detail: 'What is actually covered is decided by the numbered symbols on the dec page, not by the vehicle schedule. Symbol 1 covers any auto; symbol 7 covers only the vehicles specifically listed.',
        see: ['covered-auto-symbols', 'hired-auto', 'non-owned-auto', 'physical-damage'],
    },
    {
        id: 'workers-comp',
        term: "Workers' compensation",
        short: 'WC',
        aka: ['comp', 'work comp'],
        category: 'lines',
        essential: true,
        def: 'Pays medical costs and lost wages for employees injured at work, regardless of fault, and pays death benefits. In exchange, the employee generally cannot sue the employer.',
        detail: 'Part One pays statutory benefits with no dollar limit. Part Two — employers liability — has limits and covers the lawsuits comp does not bar. Part Three covers states listed for "other states" coverage, and leaving a state off it is a live exposure the moment somebody travels.',
        see: ['employers-liability', 'exclusive-remedy', 'experience-mod', 'other-states-coverage'],
    },
    {
        id: 'umbrella',
        term: 'Commercial umbrella',
        category: 'lines',
        essential: true,
        def: 'Extra liability limit sitting above the general liability, auto and employers liability policies — and, in some cases, covering things those policies exclude.',
        detail: 'The two features that make it an umbrella rather than excess: it can drop down and cover a claim the underlying policy excludes, subject to a self-insured retention, and it can restore an underlying aggregate that has been exhausted.',
        confused: 'An excess policy only follows the underlying form. An umbrella can be broader than what sits beneath it. In practice many policies sold as umbrellas are follow-form excess — read the form, not the name.',
        see: ['excess-liability', 'underlying-limits', 'follow-form', 'drop-down'],
    },
    {
        id: 'excess-liability',
        term: 'Excess liability',
        category: 'lines',
        def: 'Additional limit that sits above underlying policies and responds only after they are exhausted, on the underlying policies\' own terms.',
        detail: 'Excess towers are built in layers, each with its own carrier and attachment point. A gap between layers — a mismatch in the underlying limits required — leaves the insured funding the difference.',
        see: ['umbrella', 'follow-form', 'attachment-point', 'underlying-limits'],
    },
    {
        id: 'follow-form',
        term: 'Follow form',
        category: 'liability',
        def: 'An excess policy that adopts the terms, conditions and exclusions of the policy underneath it.',
        detail: '"Follow form" is rarely absolute. Most excess forms follow the underlying except as stated, and the exceptions are the whole negotiation.',
        see: ['excess-liability', 'umbrella'],
    },
    {
        id: 'attachment-point',
        term: 'Attachment point',
        category: 'limits',
        def: 'The amount of underlying limit that must be exhausted before an excess policy starts paying.',
        see: ['excess-liability', 'underlying-limits'],
    },
    {
        id: 'underlying-limits',
        term: 'Underlying limits',
        aka: ['schedule of underlying insurance'],
        category: 'limits',
        def: 'The limits the umbrella or excess policy requires the primary policies to carry.',
        detail: 'If the primary is reduced below what the umbrella requires — or an aggregate is eroded — the insured is treated as self-insured for the difference. This is a real and frequently missed gap when a primary is remarketed and the excess is not told.',
        see: ['umbrella', 'excess-liability', 'attachment-point'],
    },
    {
        id: 'drop-down',
        term: 'Drop down',
        category: 'liability',
        def: 'An umbrella responding as if it were primary — because the underlying policy excludes the claim, or because its aggregate is used up.',
        see: ['umbrella', 'sir', 'underlying-limits'],
    },
    {
        id: 'crime-insurance',
        term: 'Commercial crime',
        aka: ['fidelity', 'employee dishonesty'],
        category: 'professional',
        def: 'Covers money and property stolen from the business — by employees, by forgery, by computer fraud, by robbery.',
        detail: 'Employee theft is the coverage that matters most and is the one most often sublimited to a token amount inside a package. Social engineering — an employee tricked into wiring money to a fraudster — is usually excluded unless specifically endorsed on, and is now the most common loss of all.',
        see: ['social-engineering', 'funds-transfer-fraud', 'erisa-bond', 'fidelity-bond'],
    },
    {
        id: 'social-engineering',
        term: 'Social engineering fraud',
        aka: ['fraudulent instruction', 'business email compromise', 'bec'],
        category: 'professional',
        def: 'Loss from an employee being deceived into voluntarily sending money or data — a fake invoice, a spoofed email from the boss, a fraudulent change of bank details.',
        detail: 'The reason it needs its own coverage is that the transfer was authorised. Both crime and cyber forms exclude it by default and both will sell it back, usually sublimited to $100k–$250k and conditioned on a callback verification procedure the insured must actually follow.',
        see: ['crime-insurance', 'funds-transfer-fraud', 'cyber-liability'],
    },
    {
        id: 'funds-transfer-fraud',
        term: 'Funds transfer fraud',
        category: 'professional',
        def: 'Loss from a fraudulent instruction to a bank to transfer money out of the insured\'s account, without the insured\'s knowledge.',
        confused: 'Here the insured never authorised it. In social engineering the insured did authorise it, having been deceived. Policies cover them under different insuring agreements with different limits.',
        see: ['social-engineering', 'crime-insurance'],
    },
    {
        id: 'cyber-liability',
        term: 'Cyber liability',
        category: 'professional',
        essential: true,
        def: 'Covers the cost of a data breach or cyber attack — both the insured\'s own costs and the claims of people whose data was exposed.',
        detail: 'First-party covers breach response, forensics, notification, credit monitoring, ransom payments, and business interruption from a system outage. Third-party covers privacy liability, regulatory fines where insurable, and PCI penalties. The first-party side is what most insureds actually claim on.',
        see: ['ransomware', 'breach-response', 'tech-eo', 'property-damage'],
    },
    {
        id: 'ransomware',
        term: 'Ransomware',
        category: 'professional',
        def: 'An attack that encrypts a business\'s systems until a ransom is paid. Cyber policies cover the ransom, the negotiation, the forensics and the downtime — subject to conditions.',
        detail: 'Since 2021 carriers underwrite this hard: multi-factor authentication, offline backups, and endpoint detection are commonly warranted, and a misstatement on the ransomware supplemental is a coverage problem rather than a rating one.',
        see: ['cyber-liability', 'warranty-statement'],
    },
    {
        id: 'breach-response',
        term: 'Breach response',
        category: 'professional',
        def: 'The first-party services a cyber policy provides after an incident — legal counsel, forensics, notifying affected people, call centre, credit monitoring, public relations.',
        detail: 'Usually delivered through the carrier\'s own panel, and calling your own lawyer first can prejudice coverage. The hotline number belongs in the insured\'s incident plan, not filed with the policy.',
        see: ['cyber-liability', 'panel-counsel'],
    },
    {
        id: 'professional-liability',
        term: 'Professional liability',
        aka: ['e&o', 'errors and omissions', 'eo', 'malpractice'],
        category: 'professional',
        essential: true,
        def: 'Covers claims that the insured\'s professional work or advice was wrong, late, or negligent, causing someone else financial loss.',
        detail: 'Almost always claims-made, almost always with defence inside the limit. The CGL will not respond to bad advice — there is no bodily injury or property damage — which is why any business selling expertise needs this alongside it.',
        confused: 'E&O covers doing the work badly. General liability covers hurting someone or damaging their property while doing it.',
        see: ['claims-made', 'defense-within-limits', 'tech-eo', 'miscellaneous-professional'],
    },
    {
        id: 'tech-eo',
        term: 'Technology E&O',
        category: 'professional',
        def: 'Professional liability for technology companies, usually combined with cyber liability in one form.',
        detail: 'The combination matters: a software failure that damages a client is E&O, a breach of that client\'s data is cyber, and one incident is routinely both. Buying them from different carriers invites a coverage argument at the worst moment.',
        see: ['professional-liability', 'cyber-liability'],
    },
    {
        id: 'miscellaneous-professional',
        term: 'Miscellaneous professional liability',
        short: 'MPL',
        category: 'professional',
        def: 'E&O for professions without a dedicated form — consultants, event planners, staffing firms, property managers.',
        detail: 'The scope is set by the "professional services" definition on the dec page, which is drafted to the insured\'s own description. A vague description is a coverage gap; get it to match how the business actually earns money.',
        see: ['professional-liability'],
    },
    {
        id: 'directors-and-officers',
        term: 'Directors and officers liability',
        short: 'D&O',
        category: 'professional',
        essential: true,
        def: 'Covers claims that the people running a company made bad management decisions — brought by shareholders, investors, competitors, regulators or employees.',
        detail: 'Three sides. Side A covers individuals when the company cannot indemnify them, and is the part directors personally care about. Side B reimburses the company for indemnifying them. Side C covers the entity itself. Private-company and non-profit D&O usually bundles EPLI and fiduciary into the same form and the same shared limit.',
        confused: 'D&O covers management decisions. E&O covers the professional services the company sells to customers.',
        see: ['side-a', 'insured-vs-insured', 'epli', 'fiduciary-liability'],
    },
    {
        id: 'side-a',
        term: 'Side A coverage',
        category: 'professional',
        def: 'The part of a D&O policy that protects individual directors and officers directly, when the company is unable or unwilling to indemnify them — typically in bankruptcy or a derivative suit.',
        detail: 'Side A excess or standalone "Side A DIC" is bought specifically so a limit exists that the company\'s creditors and the entity\'s own claims cannot erode.',
        see: ['directors-and-officers'],
    },
    {
        id: 'insured-vs-insured',
        term: 'Insured versus insured exclusion',
        category: 'professional',
        def: 'A D&O exclusion for claims brought by one insured against another, aimed at collusive suits between a company and its own officers.',
        detail: 'Modern forms carve back the important exceptions — derivative suits brought by shareholders, claims by a bankruptcy trustee, claims by a former officer after a period of separation. Which carvebacks exist is a real differentiator between quotes.',
        see: ['directors-and-officers', 'exception-to-exclusion'],
    },
    {
        id: 'epli',
        term: 'Employment practices liability',
        short: 'EPLI',
        category: 'professional',
        essential: true,
        def: 'Covers claims by employees over how they were treated — wrongful termination, discrimination, harassment, retaliation, failure to promote.',
        detail: 'The CGL excludes employment-related practices and workers\' comp covers only injury, so without EPLI these claims are entirely uninsured. Defence costs dominate: most claims settle for less than they cost to defend. Third-party EPLI extends it to customers and vendors alleging discrimination or harassment by staff.',
        see: ['directors-and-officers', 'wage-and-hour', 'employers-liability'],
    },
    {
        id: 'wage-and-hour',
        term: 'Wage and hour',
        category: 'professional',
        def: 'Claims over unpaid overtime, misclassified employees, missed breaks or unpaid time.',
        detail: 'Almost universally excluded from EPLI, or covered defence-only with a small sublimit. It is the largest employment exposure most insureds have and the one they most often assume is covered.',
        see: ['epli'],
    },
    {
        id: 'fiduciary-liability',
        term: 'Fiduciary liability',
        category: 'professional',
        def: 'Covers people who administer an employee benefit plan against claims that they breached their duties under ERISA — imprudent investments, excessive fees, bad advice about benefits.',
        confused: 'Not an ERISA bond. The bond protects the plan against theft and is legally required; fiduciary liability protects the fiduciaries against lawsuits and is not.',
        see: ['erisa-bond', 'directors-and-officers'],
    },
    {
        id: 'erisa-bond',
        term: 'ERISA bond',
        aka: ['fidelity bond for plans'],
        category: 'professional',
        def: 'A bond required by federal law for anyone handling employee benefit plan funds, generally at 10% of plan assets with a $500,000 cap.',
        detail: 'It protects the plan from theft, not the fiduciary from suit, and it is a compliance item — plan sponsors get asked for it on the Form 5500 every year.',
        see: ['fiduciary-liability', 'crime-insurance'],
    },
    {
        id: 'pollution-liability',
        term: 'Pollution liability',
        aka: ['environmental liability', 'cpl'],
        category: 'lines',
        def: 'Covers clean-up costs and third-party claims from a pollution incident, which the CGL almost entirely excludes.',
        detail: 'Contractors pollution liability covers pollution caused by the insured\'s operations; site pollution covers conditions at an owned location, including pre-existing ones. Both are usually claims-made with their own retroactive dates.',
        see: ['absolute-pollution-exclusion', 'claims-made'],
    },
    {
        id: 'liquor-liability',
        term: 'Liquor liability',
        aka: ['dram shop'],
        category: 'lines',
        def: 'Covers a business that sells or serves alcohol for injuries caused by an intoxicated patron.',
        detail: 'The CGL excludes it for anyone in the business of selling alcohol. Host liquor — an office party, a business that does not sell drink — remains covered by the CGL, which is the distinction people get wrong.',
        see: ['host-liquor', 'assault-and-battery'],
    },
    {
        id: 'host-liquor',
        term: 'Host liquor liability',
        category: 'liability',
        def: 'Coverage the CGL already gives a business that serves alcohol without being in the business of selling it — a client reception, a staff party.',
        see: ['liquor-liability'],
    },
    {
        id: 'garage-liability',
        term: 'Garage liability',
        category: 'auto',
        def: 'A combined form for auto dealers and service businesses, covering both the premises operations and the autos in one policy.',
        detail: 'Garagekeepers is the separate part covering customers\' vehicles left in the insured\'s care, and it is written on a legal-liability, direct-primary or direct-excess basis — three very different promises to the customer.',
        see: ['garagekeepers', 'care-custody-control'],
    },
    {
        id: 'garagekeepers',
        term: 'Garagekeepers',
        category: 'auto',
        def: 'Covers damage to customers\' vehicles in the insured\'s care — at a repair shop, a valet service, a storage lot.',
        detail: 'Legal liability basis pays only if the insured was at fault. Direct primary pays regardless of fault. Direct excess pays after the customer\'s own policy. Contracts and customers usually assume direct primary; policies usually give legal liability.',
        see: ['garage-liability', 'personal-property-of-others'],
    },
    {
        id: 'surety-bond',
        term: 'Surety bond',
        category: 'lines',
        def: 'A three-party guarantee that the insured will perform an obligation — finish a job, pay subcontractors, comply with a licence requirement. If they do not, the surety pays and then collects from them.',
        detail: 'Not insurance in the ordinary sense: the surety expects no losses and has indemnity rights against the principal, so bonding is underwritten like credit — financial statements, working capital, character, capacity.',
        confused: 'Insurance transfers risk. A surety bond guarantees performance and is ultimately reimbursed by the person bonded.',
        see: ['performance-bond', 'payment-bond', 'fidelity-bond'],
    },
    {
        id: 'performance-bond',
        term: 'Performance bond',
        category: 'lines',
        def: 'A surety bond guaranteeing a contractor will complete a project according to the contract.',
        see: ['surety-bond', 'payment-bond', 'bid-bond'],
    },
    {
        id: 'payment-bond',
        term: 'Payment bond',
        category: 'lines',
        def: 'A surety bond guaranteeing subcontractors and suppliers get paid, protecting the project owner from liens.',
        see: ['surety-bond', 'performance-bond'],
    },
    {
        id: 'bid-bond',
        term: 'Bid bond',
        category: 'lines',
        def: 'A surety bond guaranteeing that a contractor who wins a bid will actually enter the contract and provide the performance bond.',
        see: ['surety-bond', 'performance-bond'],
    },
    {
        id: 'fidelity-bond',
        term: 'Fidelity bond',
        category: 'professional',
        def: 'Protects an employer against theft by its own employees. In modern commercial forms this is normally written as crime insurance rather than a bond.',
        see: ['crime-insurance', 'erisa-bond', 'surety-bond'],
    },
    {
        id: 'terrorism-coverage',
        term: 'Terrorism coverage',
        aka: ['tria', 'triper', 'trip'],
        category: 'lines',
        def: 'Coverage for losses from a certified act of terrorism, offered under a federal backstop programme. Carriers must offer it; insureds may reject it in writing.',
        detail: 'The signed rejection form is the file document that matters. Note the coverage responds to *certified* acts — a domestic attack never certified by the Treasury may fall outside it, which is what standalone terrorism markets exist to fix.',
        see: ['tria-rejection', 'certified-act'],
    },
    {
        id: 'product-recall',
        term: 'Product recall',
        category: 'lines',
        def: 'Covers the cost of withdrawing a defective or contaminated product — notification, retrieval, disposal, replacement, and lost income.',
        detail: 'The CGL covers damage the product causes, not the cost of getting it back. That distinction — "sistership" liability — is expressly excluded and is why food and manufacturing accounts need this separately.',
        see: ['products-completed-operations', 'cgl'],
    },
    {
        id: 'equipment-breakdown',
        term: 'Equipment breakdown',
        aka: ['boiler and machinery', 'b&m'],
        category: 'lines',
        def: 'Covers sudden mechanical or electrical failure of equipment — boilers, HVAC, refrigeration, electrical panels — including the resulting damage and lost income.',
        detail: 'Property policies exclude mechanical breakdown and electrical arcing, so this is a genuine gap rather than a duplicate. It usually brings jurisdictional boiler inspections with it, which some insureds are legally required to have anyway.',
        see: ['spoilage', 'commercial-property', 'business-income'],
    },
    {
        id: 'spoilage',
        term: 'Spoilage',
        category: 'property',
        def: 'Covers perishable stock ruined by refrigeration or equipment failure or a power interruption.',
        detail: 'Frequently sublimited to a number set years ago. For a restaurant or a grocer it should be checked against what is actually in the walk-in.',
        see: ['equipment-breakdown', 'utility-services'],
    },
    {
        id: 'utility-services',
        term: 'Utility services',
        category: 'property',
        def: 'Covers loss caused by interruption of off-premises power, water or communications — written as direct damage, time element, or both.',
        detail: 'Most forms require damage to the utility property from a covered peril, and many exclude overhead transmission lines, which is where the majority of outages actually start.',
        see: ['spoilage', 'business-income'],
    },
    {
        id: 'ordinance-or-law',
        term: 'Ordinance or law',
        category: 'property',
        essential: true,
        def: 'Pays the extra cost of rebuilding to current building codes after a covered loss — including demolishing undamaged parts the code requires you to remove.',
        detail: 'Three separate coverages: A is the loss of value of the undamaged portion, B is demolition cost, C is the increased cost of construction. Older buildings without it can be a total economic loss after a 40% fire, because the code forces a full rebuild the policy will not pay for.',
        see: ['commercial-property', 'replacement-cost'],
    },
    {
        id: 'flood',
        term: 'Flood',
        category: 'property',
        def: 'Rising water, which standard commercial property policies exclude. Covered by the NFIP up to statutory limits, or by private and excess flood markets above them.',
        detail: 'NFIP commercial limits are $500,000 building and $500,000 contents, which is nowhere near enough for most commercial buildings — excess flood over the NFIP layer is the normal structure.',
        see: ['nfip', 'dic', 'anti-concurrent-causation'],
    },
    {
        id: 'earthquake',
        term: 'Earthquake',
        category: 'property',
        def: 'Earth movement, excluded by standard property forms and bought back by endorsement or through a standalone difference-in-conditions policy.',
        detail: 'Almost always written with a percentage deductible per unit of insurance, and sometimes with separate deductibles for building, contents and business income.',
        see: ['dic', 'percentage-deductible', 'flood'],
    },
    {
        id: 'dic',
        term: 'Difference in conditions',
        short: 'DIC',
        category: 'lines',
        def: 'A standalone policy that fills the gaps in a property programme — typically adding flood and earthquake, and broadening named perils toward all-risk.',
        see: ['flood', 'earthquake', 'special-form'],
    },
    {
        id: 'cargo',
        term: 'Motor truck cargo',
        category: 'auto',
        def: 'Covers the freight a trucker is carrying against damage or loss while in their care.',
        detail: 'Written with commodity restrictions and a per-conveyance limit. Reefer breakdown and theft of targeted commodities are the two coverages most often restricted, and are exactly what a produce or electronics hauler needs.',
        see: ['ocean-marine', 'trailer-interchange', 'inland-marine'],
    },
    {
        id: 'trailer-interchange',
        term: 'Trailer interchange',
        category: 'auto',
        def: 'Physical damage coverage for a trailer the insured pulls but does not own, held under a written interchange agreement.',
        see: ['cargo', 'non-owned-auto'],
    },
];
